// core/api/useDesignations.ts
import { useState } from "react";
import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

const DESIGNATION_BASE_URL = `${API_BASE_PATH}/designation`;

const TOKEN = localStorage.getItem("authtoken");

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

const useDesignations = (_token: string | null) => {
  const [designations, setDesignations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const headers = {
    "Content-Type": "application/json",
    authorization: "Bearer " + _token,
  };

  // Fetch all designations
  const fetchDesignations = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(DESIGNATION_BASE_URL, { headers });
      const normalized = res.data.map((item: any) => ({
        designation_name: item["designation_name"],
        department_name: item["department_name"],
        status: item["status"]?.toLowerCase() || "inactive",
        department: item["department"],
      }));

      setDesignations(normalized);
    } catch (err: any) {
      console.error("[Fetch Designations] Error:", err);
      toast.error(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const addDesignation = async (data: any) => {
    try {
      const res = await axios.post(DESIGNATION_BASE_URL, data, { headers });
      return res.data;
    } catch (err: any) {
      console.error("[Add Designation] Error:", err);
      toast.error(extractErrorMessage(err));
      throw err;
    }
  };

  const updateDesignation = async (name: string, data: any) => {
    const encodedName = encodeURIComponent(name);
    const url = `${DESIGNATION_BASE_URL}/${encodedName}`;
    try {
      const res = await axios.put(url, data, { headers });
      return res.data;
    } catch (err: any) {
      console.error("[Update Designation] Error:", err);
      toast.error(extractErrorMessage(err));
      throw err;
    }
  };

  const deleteDesignation = async (
    departmentName: string,
    designationName: string
  ) => {
    const encodedDepartmentName = encodeURIComponent(departmentName);
    const encodedDesignationName = encodeURIComponent(designationName);
    const url = `${DESIGNATION_BASE_URL}/designation/${encodedDesignationName}`;

    try {
      const res = await axios.delete(url, { headers });
      return res.data;
    } catch (err: any) {
      console.error("[Delete Designation] Error:", err);
      toast.error(extractErrorMessage(err));
      throw err;
    }
  };

  const fetchDesignationsAllInfo = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${DESIGNATION_BASE_URL}/all/info`, {
        headers,
      });
      const normalized = res.data.map((item: any) => ({
        designation: item["Designation"],
        department: item["Department"],
        totalEmployees: item["Total Employees"],
        status: item["Status"]?.toLowerCase() || "inactive",
        createdAt: item["Created At"],
      }));
      return normalized;
    } catch (err: any) {
      console.error("[Fetch Designations All Info] Error:", err);
      toast.error(extractErrorMessage(err));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    designations,
    isLoading,
    fetchDesignations,
    addDesignation,
    updateDesignation,
    deleteDesignation,
    fetchDesignationsAllInfo,
  };
};

export default useDesignations;
